/* Motion for The Story.

   Four ideas, and nothing else moves. The page has long stretches —
   the whole Pakistan passage, the founder note, the closing — where
   the answer to "what animates here" is deliberately nothing.

   Deliberately NOT az-leaves: falling particles are the homepage's
   idea, and repeating it here would make two rooms of the same house
   look like the same room. Everything below comes from what the page
   is actually made of — a page settling, a pressed flower shifting,
   a garden plan being ruled out, a print coming up.

   Same two rules the rest of the Azahrah motion layer follows:

   1. Nothing is hidden or displaced by the stylesheet alone. Every
      resting state is armed here, in JS, so a blocked asset leaves a
      finished, static, correct page rather than a broken one.

   2. prefers-reduced-motion is checked once, up front, and returns
      before anything is armed — so reduced motion is the plain page,
      not the page with transitions set to 0s.
*/

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const COARSE_MAX = 749;
const isPhone = () => window.innerWidth <= COARSE_MAX;

/* ---------------------------------------------------------------
   1. The opening settles.

   The tent panel starts a little low and a little large, and rises
   into alignment with the title over one long beat. Type does not
   move: the image finds the line, not the other way round.
   --------------------------------------------------------------- */
function settle(root) {
  const el = root.querySelector('[data-az-settle]');
  if (!el) return;
  el.dataset.azSettle = 'armed';
  // Two frames: one for the armed state to be painted, one to leave it.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.dataset.azSettle = 'settled';
    });
  });
}

/* ---------------------------------------------------------------
   2. Botanical drift.

   Two elements on the whole page. Each maps its distance through the
   viewport onto a few pixels of travel and, at most, a degree of
   rotation — the amount a pressed flower moves when the page it is
   lying on is turned, not the amount confetti moves.

   One rAF loop, one scroll listener, reads batched before writes.
   --------------------------------------------------------------- */
function drift(root) {
  const items = [...root.querySelectorAll('[data-az-drift]')];
  if (!items.length) return;

  let ticking = false;

  const write = () => {
    ticking = false;
    const h = window.innerHeight;

    for (const el of items) {
      const box = el.getBoundingClientRect();
      if (box.bottom < -200 || box.top > h + 200) continue;

      // Progress of the element THROUGH the viewport, not the distance
      // of its centre from the middle of the screen. The album page is
      // 1500px tall; measured from its centre it never gets near the
      // middle of a 900px window, so the centre-distance version pinned
      // at -1 and the drift became a static offset. Normalising over
      // (viewport + element) sweeps +1 to -1 for any element height.
      const p = (box.top + box.height) / (h + box.height);
      const t = Math.max(-1, Math.min(1, 2 * p - 1));

      const y = Number(el.dataset.driftY || 14) * t;
      const r = Number(el.dataset.driftR || 0) * t;
      el.style.setProperty('--az-drift', `translate3d(0, ${y.toFixed(2)}px, 0) rotate(${r.toFixed(3)}deg)`);
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(write);
  };

  write();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}

/* ---------------------------------------------------------------
   3. The garden plan is ruled out.

   A charbagh reduced to its lines — the enclosure, the two channels
   that cross, the tank where they meet — drawn once as the section
   arrives, then left as a watermark. One instance on the page. It is
   a diagram being drawn, not a pattern being tiled.

   The dash length is measured from the live path rather than guessed,
   so the stroke length is right whatever the SVG is scaled to.
   --------------------------------------------------------------- */
function plan(root) {
  const svg = root.querySelector('[data-az-plan]');
  if (!svg) return;

  const strokes = [...svg.querySelectorAll('path, line, rect, circle')];
  if (!strokes.length) return;

  strokes.forEach((s, i) => {
    const len = s.getTotalLength ? s.getTotalLength() : 0;
    if (!len) return;
    s.style.strokeDasharray = len;
    s.style.strokeDashoffset = len;
    // Enclosure first, then channels, then the tank — the order you
    // would actually draw it in.
    s.style.transitionDelay = `${i * 260}ms`;
  });

  svg.dataset.azPlan = 'armed';

  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      svg.dataset.azPlan = 'drawn';
      io.disconnect();
    },
    { rootMargin: '0px 0px -20% 0px' }
  );
  io.observe(svg);
}

/* ---------------------------------------------------------------
   4. The print comes up.

   The one photograph that gets a reveal, wiped from its left edge
   rather than faded up — a print being drawn out of the tray, or a
   page being turned over. Used once, on purpose.
   --------------------------------------------------------------- */
function wipe(root) {
  const items = [...root.querySelectorAll('[data-az-wipe]')];
  if (!items.length) return;

  // Same reasoning as az-reveal's phone trigger: a negative margin
  // holds the wipe until the photograph is a sixth of the way up the
  // screen, which on a desktop scroll is the moment it is worth
  // watching and on a thumb-scroll is a second of it still uncovering
  // while the reader has already scrolled past. On a phone it starts
  // just before the plate reaches the fold instead.
  const phone = isPhone();

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.dataset.azWipe = 'open';
        io.unobserve(e.target);
      }
    },
    { rootMargin: phone ? '0px 0px 10% 0px' : '0px 0px -15% 0px' }
  );

  for (const el of items) {
    // Anything already on screen at upgrade time is left alone, so a
    // deep link or a restored scroll position never wipes a photograph
    // the reader is already looking at.
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) continue;
    el.dataset.azWipe = 'shut';
    io.observe(el);
  }
}

class AzStoryPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready !== undefined) return;
    this.dataset.ready = '';

    if (REDUCED.matches) return;

    settle(this);
    plan(this);
    wipe(this);

    // Drift is desktop-only: on a phone these elements are either gone
    // or are carrying text, and a few pixels of parallax is not worth a
    // scroll listener on a device that is composited harder already.
    if (!isPhone()) this.stopDrift = drift(this);
  }

  disconnectedCallback() {
    this.stopDrift?.();
  }
}

if (!customElements.get('az-story-page')) {
  customElements.define('az-story-page', AzStoryPage);
}
